import { RetryOptions } from '@reduxjs/toolkit/dist/query/retry'
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query/react'
import { AuthToken, AuthTokenArgs } from '@typesApp/Customer'
import { ENDPOINTS } from '@constants/index'
import { config } from '@config/config'

const MAX_RETRY_COUNT = 5

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // retry with backoff
  const retryOptions: RetryOptions = {
    retryCondition: (error, args, extraArgs): boolean => {
      return (
        // NOTE: status will be FETCH_ERROR if some local network conditions (like VPN) changes
        // and we do want to retry again in this case. No harm since we have a max retry count.
        (error.status === 409 || error.status === 500 || error.status === 504 || error.status === 'FETCH_ERROR') &&
        extraArgs.attempt <= MAX_RETRY_COUNT
      )
    },
  }

  const dbBaseQuery = retry(
    fetchBaseQuery({
      baseUrl: config.getApiHost(),
    }),
    retryOptions
  )

  return await dbBaseQuery(args, api, extraOptions)
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: authBaseQuery,
  keepUnusedDataFor: 300,
  endpoints: builder => ({
    getAuthToken: builder.mutation<AuthToken, AuthTokenArgs>({
      query: body => ({
        url: ENDPOINTS.DIRECT_BILLING.AUTH_TOKEN,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': `${config.getApiKey('getAuthToken')}`,
        },
      }),
      transformResponse: (returnValue: AuthToken, meta, args) => {
        return {
          ...returnValue,
          customerType: args.customerType,
        }
      },
    }),
  }),
})

export const { useGetAuthTokenMutation } = authApi
